import { Injectable, Injector, inject, signal } from '@angular/core';
import { Observable, catchError, debounceTime, distinctUntilChanged, forkJoin, map, of, switchMap } from 'rxjs';
import { ConfigService } from '../core/config/config.service';
import { ClienteService } from './cliente.service';
import { TenantService } from './tenant.service';

export const BUSCA_MIN_CARACTERES = 2;
export const BUSCA_DEBOUNCE_MS = 250;
export const BUSCA_TAMANHO = 5;

export interface ItemBusca {
  rotulo: string;
  detalhe: string | null;
  rota: string;
}

/** Resultado de um grupo; `erro` não derruba os demais grupos. */
export interface GrupoBusca {
  itens: ItemBusca[];
  erro: boolean;
}

export interface ResultadoBusca {
  clientes: GrupoBusca;
  /** `null` quando a flag `features.tenants` está desligada. */
  tenants: GrupoBusca | null;
}

/** Estado da paleta de busca global e consulta dos grupos remotos. Não persiste termos nem loga dados. */
@Injectable({ providedIn: 'root' })
export class BuscaGlobalService {
  private readonly injector = inject(Injector);
  private readonly config = inject(ConfigService);

  readonly aberta = signal(false);
  private origem: HTMLElement | null = null;

  abrir(): void {
    if (this.aberta()) {
      return;
    }
    const ativo = document.activeElement;
    this.origem = ativo instanceof HTMLElement && ativo !== document.body ? ativo : null;
    this.aberta.set(true);
  }

  fechar(devolverFoco = true): void {
    if (!this.aberta()) {
      return;
    }
    this.aberta.set(false);
    if (devolverFoco) {
      this.origem?.focus();
    }
    this.origem = null;
  }

  alternar(): void {
    if (this.aberta()) {
      this.fechar();
    } else {
      this.abrir();
    }
  }

  /**
   * Debounce de 250 ms, mínimo de 2 caracteres e cancelamento da requisição anterior (`switchMap`).
   * Termos curtos emitem `null` sem chamar o backend.
   */
  consultar(termo$: Observable<string>): Observable<ResultadoBusca | null> {
    return termo$.pipe(
      map((t) => t.trim()),
      debounceTime(BUSCA_DEBOUNCE_MS),
      distinctUntilChanged(),
      switchMap((q) => (q.length < BUSCA_MIN_CARACTERES ? of(null) : this.buscarGrupos(q))),
    );
  }

  private buscarGrupos(q: string): Observable<ResultadoBusca> {
    const falha = (): Observable<GrupoBusca> => of({ itens: [], erro: true });
    const clientes$: Observable<GrupoBusca> = this.injector
      .get(ClienteService)
      .listar({ busca: q }, 0, BUSCA_TAMANHO)
      .pipe(
        map((p) => ({
          itens: p.conteudo.map((c) => ({
            rotulo: c.nomeFantasia ? `${c.razaoSocial} (${c.nomeFantasia})` : c.razaoSocial,
            detalhe: c.cnpjFormatado,
            rota: `/clientes/${c.id}`,
          })),
          erro: false,
        })),
        catchError(falha),
      );
    const tenants$: Observable<GrupoBusca | null> =
      this.config.settings()?.features?.tenants === true
        ? this.injector
            .get(TenantService)
            .listar({ busca: q }, 0, BUSCA_TAMANHO)
            .pipe(
              map((p) => ({
                itens: p.conteudo.map((t) => ({
                  rotulo: t.slug,
                  detalhe: t.clienteRazaoSocial,
                  rota: `/tenants/${t.id}`,
                })),
                erro: false,
              })),
              catchError(falha),
            )
        : of(null);
    return forkJoin({ clientes: clientes$, tenants: tenants$ });
  }
}
